import { useEffect, useState, useContext, type FC, type ReactNode, type MouseEvent } from 'react';
import { ReactRelayContext } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage, useIntl } from 'dibs-react-intl';
import {
    getRecentHistoryList,
    userFragment,
} from 'dibs-recent-history/exports/getRecentHistory.ts';

import { getSectionPropsByType, createSearchLink } from '../../utils/sharedSearchBarHelpers.ts';
import { unmaskFragmentData } from '../../helpers/relayUnmaskingHelper.tsx';
import { useHeaderFooterServerVars } from '../HeaderFooterServerVarsContext/HeaderFooterServerVarsContext.tsx';
import { SearchBarSection } from './SearchBarSection.tsx';
import { SearchBarTerm } from './SearchBarTerm.tsx';
import { SearchBarRecentlyViewedItems } from './SearchBarRecentlyViewedItems.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { type Suggestions } from '../../hooks/useSearchSuggestions.ts';
import {
    type ItemType,
    type UserRecentHistoryItemType,
} from 'dibs-recent-history/exports/types.ts';

import dibsCss from 'dibs-css';
import styles from './SearchBar-style.module.css';

const TERM_SECTION_TYPE_RECENT = 'RECENT';
const titleMessage = (
    <FormattedMessage id="dbl.header.searchBar.recentlyViewed" defaultMessage="Recently Viewed" />
);

export const getSuggestedTermElements = ({
    suggestedTerm,
    searchTerm,
}: {
    suggestedTerm: string;
    searchTerm?: string;
}): ReactNode => {
    if (!searchTerm) {
        return suggestedTerm;
    }
    const searchTermParts = searchTerm.split(' ');
    const suggestedTermParts = suggestedTerm.split(/(\s+)/);
    return suggestedTermParts.reduce<ReactNode[]>((suggestedTermEls, suggestedTermPart, i) => {
        let maxSearchTermPartLength = 0;
        let unboldedSuggestedTermPart;
        let boldedSuggestedTermPart;
        searchTermParts.forEach(searchTermPart => {
            const matchingIndex = suggestedTermPart
                .toLowerCase()
                .indexOf(searchTermPart.toLowerCase());
            if (matchingIndex === 0 && searchTermPart.length > maxSearchTermPartLength) {
                maxSearchTermPartLength = searchTermPart.length;
                unboldedSuggestedTermPart = suggestedTermPart.substr(0, searchTermPart.length);
                boldedSuggestedTermPart = suggestedTermPart.substr(searchTermPart.length);
            }
        });
        if (unboldedSuggestedTermPart) {
            suggestedTermEls.push(unboldedSuggestedTermPart);
        }
        if (boldedSuggestedTermPart) {
            suggestedTermEls.push(
                <span
                    key={`${suggestedTerm}${suggestedTermPart}${i}`}
                    className={dibsCss.textBodyMediumHeavy}
                >
                    {boldedSuggestedTermPart}
                </span>
            );
        }

        if (!boldedSuggestedTermPart && !unboldedSuggestedTermPart) {
            suggestedTermEls.push(
                <span
                    key={`${suggestedTerm}${suggestedTermPart}${i}`}
                    className={dibsCss.textBodyMediumHeavy}
                >
                    {suggestedTermPart}
                </span>
            );
        }

        return suggestedTermEls;
    }, []);
};

type UnmaskedUserRecentHistory = {
    recentHistoryItems?: {
        edges?: { node: UserRecentHistoryItemType }[];
    } | null;
};

type Props = {
    visible: boolean;
    suggestions: Suggestions;
    handleRowClick: (term: string, href: string, event: MouseEvent) => void;
    cursorHighlightId?: string;
    focusOnOverlayScroll?: boolean;
    canShowRecentlyViewed?: boolean;
    searchTerm?: string;
    user?: unknown | null;
};

export const SearchBarOverlay: FC<Props> = ({
    visible,
    suggestions,
    handleRowClick,
    cursorHighlightId,
    focusOnOverlayScroll = false,
    canShowRecentlyViewed,
    searchTerm,
    user,
}) => {
    const intl = useIntl();
    const relayContext = useContext(ReactRelayContext);
    const { isMobile } = useHeaderFooterServerVars();

    const [recentlyViewedItems, setRecentlyViewedItems] = useState<ItemType[]>([]);
    const [userRecentHistoryItems, setUserRecentHistoryItems] = useState<
        { node: UserRecentHistoryItemType }[]
    >([]);

    if (relayContext && user !== null && !userRecentHistoryItems) {
        const { recentHistoryItems } =
            unmaskFragmentData<UnmaskedUserRecentHistory | null>({
                relayContext,
                relayData: user,
                fragment: userFragment.user,
            }) || {};

        // if recentHistoryItems is undefined that means that its still being fetched, in other falsy cases it will be null or empty arr
        if (recentHistoryItems !== undefined) {
            const edges = (recentHistoryItems?.edges || []).filter(filterFalsy);
            setUserRecentHistoryItems(edges);
        }
    }

    const showRecentlyViewedItems = canShowRecentlyViewed && recentlyViewedItems.length > 0;
    const hasSuggestions = !!suggestions?.some(suggestion => suggestion?.terms?.length);

    useEffect(() => {
        const recentHistoryItemsList = getRecentHistoryList({
            recentHistoryItems: (userRecentHistoryItems || []).map(({ node }) => node),
            isClient: true,
            maxItems: !isMobile ? 8 : 5,
        });

        setRecentlyViewedItems(recentHistoryItemsList);
    }, [isMobile, userRecentHistoryItems]);

    if (!hasSuggestions && !showRecentlyViewedItems) {
        return null;
    }

    return (
        <div
            aria-live={visible ? 'assertive' : 'off'}
            data-tn="search-bar-overlay"
            className={classnames(dibsCss.bgWhite, dibsCss.pbSmall, {
                [styles.dropdownWrapper]: !isMobile,
                [dibsCss.sassyBoxShadow]: !isMobile,
                [dibsCss.gapXsmall]: !isMobile,
                [styles.mobileScrollable]: isMobile,
                [dibsCss.overflowYAuto]: isMobile,
                [dibsCss.hidden]: !visible,
            })}
            onTouchStart={
                focusOnOverlayScroll
                    ? () => {
                          const active = document.activeElement;
                          if (active instanceof HTMLElement) {
                              active.blur();
                          }
                      }
                    : () => {}
            }
        >
            {isMobile && showRecentlyViewedItems && (
                <SearchBarSection key="recentlyViewed" title={titleMessage}>
                    <SearchBarRecentlyViewedItems recentlyViewedItems={recentlyViewedItems} />
                </SearchBarSection>
            )}
            {(suggestions || []).map(section => {
                const type = section?.type || '';
                const terms = (section?.terms || []).filter(filterFalsy);
                const { title, urlSection } = getSectionPropsByType(type, intl);
                if (!terms.length) {
                    return null;
                }
                return (
                    <SearchBarSection key={title} title={title}>
                        {terms.map(({ term, urlLabel }, index) => {
                            const termElements =
                                type === TERM_SECTION_TYPE_RECENT
                                    ? term
                                    : getSuggestedTermElements({
                                          suggestedTerm: term || '',
                                          searchTerm,
                                      });
                            const href = createSearchLink({
                                term: term || '',
                                urlSection,
                                urlLabel,
                                type,
                            });
                            return (
                                <SearchBarTerm
                                    key={term}
                                    id={`${term}-${type}-${index}`}
                                    termElements={termElements}
                                    term={term || ''}
                                    type={type}
                                    href={href}
                                    handleRowClick={handleRowClick}
                                    cursorHighlightId={cursorHighlightId}
                                />
                            );
                        })}
                    </SearchBarSection>
                );
            })}
            {!isMobile && showRecentlyViewedItems && (
                <SearchBarSection key="recentlyViewed" title={titleMessage}>
                    <SearchBarRecentlyViewedItems recentlyViewedItems={recentlyViewedItems} />
                </SearchBarSection>
            )}
        </div>
    );
};
